import { useGate, useUnit } from 'effector-react'
import Head from 'next/head'
import React from 'react'
import { ReflexContainer, ReflexElement, ReflexSplitter } from 'react-reflex'
import UAParser from 'ua-parser-js'

import { css } from '@portal/styled-system/css'

import { $isChatOpen } from '@/entities/chat'
import { $clientRuntime, $gameConnectResult } from '@/entities/game'
import { useLanguageRedirectPortal } from '@/entities/language'
import { $portalProfile } from '@/entities/profile/portal'
import { isFeatureEnabled } from '@/shared/config'
import { getAssetUrl } from '@/shared/lib/cdn'
import { useI18n } from '@/shared/lib/i18n'
import { BackgroundImage } from '@/shared/ui'
import { KoreanAgeRatingBadge } from '@/shared/ui/korean-age-rating-badge'
import { ChatContainer } from '@/widgets/chat'
import { NavbarPlay } from '@/widgets/layout/navbar'
import { PageHead } from '@/widgets/layout/seo'

import 'react-reflex/styles.css'

import * as model from './model'
import { gameLayout } from './recipe'
import { LoginFail, Maintenance, PlayThrobber, playUiAssets } from './ui'

export function PlayPage() {
    useLanguageRedirectPortal()
    useGate(model.PlayPageGate)

    return (
        <PlayPageLayout>
            <PlayContent />
        </PlayPageLayout>
    )
}

export function PlayPageLayout({ children }: React.PropsWithChildren) {
    const isGameScreenVisible = useUnit(model.$isPreloaderReady)
    const isChatOpen = useUnit($isChatOpen)
    const handleChatWidthChange = useUnit(model.chatWidthChanged)
    const chatWidth = useUnit(model.$chatWidth)

    return (
        <React.Fragment>
            <PageHead path="play/" />
            <AssetsPreload />
            <NavbarPlay />
            <KoreanAgeRatingBadge show={isGameScreenVisible} />

            <BackgroundImage src="/assets/taongaBeach.jpg">
                <div className={css({ display: 'flex' })}>
                    <ReflexContainer orientation="vertical" windowResizeAware>
                        <ReflexElement
                            className={css({
                                overflow: 'unset!',
                            })}
                        >
                            <GameLayout>{children}</GameLayout>
                        </ReflexElement>

                        {isChatOpen && (
                            <ReflexSplitter
                                className={css({
                                    border: 'none!',
                                    userSelect: 'none!',
                                    width: '[1px!]',

                                    _after: {
                                        content: '""',
                                        position: 'absolute',
                                        width: '2',
                                        height: 'full',
                                    },
                                })}
                            />
                        )}
                        {isChatOpen && (
                            <ReflexElement
                                minSize={300}
                                size={chatWidth}
                                maxSize={640}
                                onResize={(props) => {
                                    const element = props.domElement as HTMLElement
                                    handleChatWidthChange(element.offsetWidth)
                                }}
                                className={css({
                                    overflow: 'unset!',
                                })}
                            >
                                <ChatContainer />
                            </ReflexElement>
                        )}
                    </ReflexContainer>
                </div>
            </BackgroundImage>
        </React.Fragment>
    )
}

// Keep connection-error visuals available if the network drops after the page opens.
function AssetsPreload() {
    return (
        <Head>
            {Object.values(playUiAssets).map((asset) => (
                <link key={asset} rel="preload" as="image" fetchPriority="low" href={getAssetUrl(asset)} />
            ))}
        </Head>
    )
}

const isPlatformMobile = (): boolean => {
    const device = new UAParser().getDevice()

    return device.type === 'mobile' || device.type === 'tablet'
}

function GameLayout({ children }: React.PropsWithChildren) {
    const [isMobile, setIsMobile] = React.useState(false)
    const classes = gameLayout({ isMobile: isMobile && isFeatureEnabled('mobile-layout') })

    React.useEffect(() => {
        if (isPlatformMobile()) {
            setIsMobile(true)
        }
    }, [])

    return (
        <div className={classes.container}>
            <div className={classes.content}>
                <BackgroundImage src="/assets/preloaderCurtain_beach.jpg" styles={{ height: 'full' }}>
                    {children}
                </BackgroundImage>
            </div>
        </div>
    )
}

function PlayContent() {
    const { lang } = useI18n()

    const profile = useUnit($portalProfile)
    const isGameScreenVisible = useUnit(model.$isPreloaderReady)
    const connectResult = useUnit($gameConnectResult)
    const clientRuntime = useUnit($clientRuntime)

    if (connectResult?.type === 'maintenance') {
        return <Maintenance type={connectResult.maintenance.type} />
    }

    if (connectResult?.type === 'error') {
        return <LoginFail />
    }

    return (
        <React.Fragment>
            {profile && (
                <iframe
                    id="Main"
                    name="Main"
                    key={`${lang}_${profile.publicId}_${clientRuntime}`}
                    src={connectResult?.config.client_url}
                    width="100%"
                    height="100%"
                    allowFullScreen={true}
                    className={css({
                        width: 'full',
                        padding: '0',
                        display: 'block',
                        zIndex: 4,
                        border: 'none',
                    })}
                />
            )}
            {!isGameScreenVisible && <PlayThrobber />}
        </React.Fragment>
    )
}
